import { useCallback, useState, useMemo, useRef, useLayoutEffect, useEffect } from "react";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  closestCorners,
  type CollisionDetection,
  type DragStartEvent,
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type UniqueIdentifier,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { createGridSortingStrategy } from "./gridSortingStrategy";
import { X, Minus, Plus, LayoutGrid, Trash2, PencilIcon } from "lucide-react";
import { Editable, EditableArea, EditableInput, EditablePreview, EditableTrigger } from "@/modern/components/ui/editable";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modern/components/ui/select";
import { useConfiguratorContext } from "../context/DashboardConfiguratorContext";
import { ConfiguratorTile } from "./ConfiguratorTile";
import { TILE_COLORS, FONT_PRESETS, LAYOUT_PRESETS, isDefaultPreset } from "../constants";
import type { TileConfig, FontPresetId } from "../types";
import type { WidgetId } from "@/other/constants/EntityManagerConfig";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { HiddenTilesSidebar } from "./HiddenTilesSidebar";
import { cn } from "@/modern/utils";

interface ConfiguratorGridProps {
  onClose: () => void;
}

function VisibleGridDroppable({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: "visible-droppable" });
  return (
    <div
      ref={setNodeRef}
      className={cn("min-h-full", isOver && "ring-2 ring-inset ring-primary/30 rounded-lg", className)}
    >
      {children}
    </div>
  );
}

export function ConfiguratorGrid({ onClose }: ConfiguratorGridProps) {
  const {
    columns,
    visibleTiles,
    hiddenTiles,
    activeLayoutPreset,
    activeFontPreset,
    customPresets,
    hasDraftChanges,
    handleReorder,
    handleResize,
    handleToggleVisibility,
    handleMoveBetweenContainers,
    handleCloseConfigurator,
    handleColumnChange,
    handleFontPresetChange,
    handleUpdateCustomPreset,
    handleSaveAsNewPreset,
    handleDeleteCustomPreset,
  } = useConfiguratorContext();

  const confirm = useConfirm();
  const isCustomPreset = !isDefaultPreset(activeLayoutPreset);

  const handleDeleteWithConfirm = useCallback(async () => {
    const confirmed = await confirm({
      title: "Delete preset?",
      description: "Are you sure you want to delete this preset? This action cannot be undone.",
      customActions: ({ confirm: ok, cancel }: { confirm: () => void; cancel: () => void }) => (
        <><Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button><Button variant="destructive" size="sm" onClick={ok}>Delete</Button></>
      ),
    });
    if (confirmed) handleDeleteCustomPreset(activeLayoutPreset);
  }, [confirm, handleDeleteCustomPreset, activeLayoutPreset]);

  const activePresetName = useMemo(() => {
    return (
      LAYOUT_PRESETS.find((p) => p.id === activeLayoutPreset)?.name ??
      customPresets.find((p) => p.id === activeLayoutPreset)?.name ??
      "Dashboard"
    );
  }, [activeLayoutPreset, customPresets]);

  const [presetName, setPresetName] = useState(activePresetName);
  const handleNameChange = useCallback((val: string) => setPresetName(val), []);
  const [nameError, setNameError] = useState(false);

  // Reset when user switches to a different preset or when preset name changes externally
  useEffect(() => {
    setPresetName(activePresetName);
  }, [activeLayoutPreset, activePresetName]);

  // Auto-clear the name error after 3 s
  useEffect(() => {
    if (!nameError) return;
    const id = setTimeout(() => setNameError(false), 3000);
    return () => clearTimeout(id);
  }, [nameError]);
  const hiddenSidebarRef = useRef<HTMLDivElement | null>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeTileRect, setActiveTileRect] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [dragOverlayPortalReady, setDragOverlayPortalReady] = useState(false);

  useLayoutEffect(() => {
    setDragOverlayPortalReady(true);
  }, []);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 3 } }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const p = args.pointerCoordinates;
    const sidebarEl = hiddenSidebarRef.current;
    if (p && sidebarEl) {
      const r = sidebarEl.getBoundingClientRect();
      if (
        p.x >= r.left &&
        p.x <= r.right &&
        p.y >= r.top &&
        p.y <= r.bottom
      ) {
        return [{ id: "hidden-droppable" }];
      }
    }
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return closestCorners(args);
  }, []);

  const visibleTileIds = useMemo(
    () => visibleTiles.map((t) => t.id),
    [visibleTiles]
  );

  // Recreated only when tile spans or column count change (not during drag).
  const gridStrategy = useMemo(
    () => createGridSortingStrategy(visibleTiles, columns, 8, 100),
    [visibleTiles, columns]
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveId(event.active.id);
      const target = event.activatorEvent.target as HTMLElement | null;
      const tileEl = target?.closest("[data-configurator-tile]") as HTMLElement | null;
      if (tileEl) {
        const rect = tileEl.getBoundingClientRect();
        setActiveTileRect({ width: rect.width, height: rect.height });
      } else {
        setActiveTileRect(null);
      }
    },
    []
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setActiveTileRect(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveId(null);
      setActiveTileRect(null);

      if (!over) return;

      const draggedId = active.id as WidgetId;
      const overId = over.id;

      const isActiveVisible = visibleTiles.some((t) => t.id === draggedId);
      const isActiveHidden = hiddenTiles.some((t) => t.id === draggedId);

      const isOverHiddenContainer = overId === "hidden-droppable";
      const isOverVisibleContainer = overId === "visible-droppable";
      const isOverVisibleTile = visibleTiles.some((t) => t.id === overId);

      if (isActiveVisible && isOverHiddenContainer) {
        handleToggleVisibility(draggedId);
        return;
      }

      if (isActiveHidden && (isOverVisibleContainer || isOverVisibleTile)) {
        handleMoveBetweenContainers(draggedId, true, isOverVisibleTile ? (overId as WidgetId) : undefined);
        return;
      }

      // Visible → visible: commit the reorder now (strategy provided the visual preview).
      if (isActiveVisible && isOverVisibleTile) {
        const activeIdx = visibleTiles.findIndex((t) => t.id === draggedId);
        const overIdx = visibleTiles.findIndex((t) => t.id === overId);
        if (activeIdx !== -1 && overIdx !== -1 && activeIdx !== overIdx) {
          handleReorder(arrayMove(visibleTiles, activeIdx, overIdx));
        }
      }
    },
    [
      visibleTiles,
      hiddenTiles,
      handleReorder,
      handleToggleVisibility,
      handleMoveBetweenContainers,
    ]
  );

  const activeTile = useMemo(() => {
    if (!activeId) return null;
    return (
      visibleTiles.find((t) => t.id === activeId) ??
      hiddenTiles.find((t) => t.id === activeId) ??
      null
    );
  }, [activeId, visibleTiles, hiddenTiles]);

  const handleClose = useCallback(() => {
    handleCloseConfigurator(onClose);
  }, [handleCloseConfigurator, onClose]);

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b flex-shrink-0 gap-2">
        {/* Left zone: editable preset name */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1 relative">
          <Editable
            value={presetName}
            onValueChange={handleNameChange}
            onSubmit={(value) => {
              if (!value.trim()) setPresetName(activePresetName);
            }}
            placeholder="Enter preset name..."
            triggerMode="click"
            className="flex-row items-center group min-w-0"
          >
            <EditableArea className="flex items-center min-w-0">
              <EditablePreview
                className={cn(
                  "!text-xl font-semibold truncate p-0 pb-px border-0 rounded-none border-b-[1px] border-dashed",
                  nameError ? "border-destructive" : "border-muted-foreground"
                )}
              />
              <EditableInput
                className={cn(
                  "!text-xl font-semibold p-0 pb-px pl-1 min-w-[120px]",
                  nameError && "border-destructive"
                )}
              />
            </EditableArea>
            <EditableTrigger asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <PencilIcon className="size-4" />
              </Button>
            </EditableTrigger>
          </Editable>
        </div>

        {/* Right zone: layout controls + action buttons + close */}
        <div className="flex items-center gap-3 shrink-0">
          {/* Columns label + stepper */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground font-medium select-none">Columns</span>
            <div className="flex items-center h-8 border border-input rounded-md overflow-hidden bg-background">
              <button
                className="px-2.5 h-full border-r border-input hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center text-foreground"
                onClick={() => handleColumnChange(Math.max(1, columns - 1))}
                disabled={columns <= 1}
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="w-8 text-center text-xs font-medium tabular-nums select-none text-foreground">
                {columns}
              </span>
              <button
                className="px-2.5 h-full border-l border-input hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center text-foreground"
                onClick={() => handleColumnChange(Math.min(4, columns + 1))}
                disabled={columns >= 4}
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* Font label + select */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground font-medium select-none">Font</span>
            <Select
              value={activeFontPreset}
              onValueChange={(val) => handleFontPresetChange(val as FontPresetId)}
            >
              <SelectTrigger className="!h-8 w-[110px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONT_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {preset.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ButtonGroup: Delete + Save As + Save */}
          <ButtonGroup>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 !bg-transparent hover:!bg-accent text-destructive hover:text-destructive disabled:text-destructive/40"
              onClick={handleDeleteWithConfirm}
              disabled={!isCustomPreset}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 !bg-transparent hover:!bg-accent"
              onClick={() => {
                const name = presetName.trim();
                if (!name || name === activePresetName) {
                  setNameError(true);
                  toast.error("The new preset must have a different name");
                  return;
                }
                handleSaveAsNewPreset(name);
                onClose();
              }}
            >
              Save As
            </Button>
            {isCustomPreset && (
              <Button
                size="sm"
                className="h-8"
                onClick={() => { handleUpdateCustomPreset(presetName.trim()); onClose(); }}
                disabled={!hasDraftChanges && presetName.trim() === activePresetName}
              >
                Save
              </Button>
            )}
          </ButtonGroup>

          {/* Standalone close */}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Grid + Hidden Sidebar — no modifiers: restrictToWindowEdges skewed overlay vs cursor */}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 flex min-w-0 overflow-hidden">
          <ScrollArea className="flex-1 p-4">
            {visibleTiles.length > 0 ? (
              <SortableContext
                items={visibleTileIds}
                strategy={gridStrategy}
              >
                <VisibleGridDroppable>
                  <div className="relative min-h-full">
                    {/* Background grid lines */}
                    <div
                      className="absolute inset-0 grid pointer-events-none"
                      style={{
                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                        gridTemplateRows: `repeat(5, calc(100px + 8px))`,
                      }}
                    >
                      {Array.from({ length: columns * 5 }).map((_, i) => {
                        const col = i % columns;
                        const row = Math.floor(i / columns);
                        return (
                          <div
                            key={i}
                            className="border-white/[0.08]"
                            style={{
                              borderStyle: "dashed",
                              borderWidth: 0,
                              borderRightWidth: col < columns - 1 ? 1 : 0,
                              borderBottomWidth: row < 4 ? 1 : 0,
                            }}
                          />
                        );
                      })}
                    </div>

                    {/* Tile grid */}
                    <div
                      data-grid
                      className="relative z-[1] grid gap-2 min-h-full overflow-hidden"
                      style={{
                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                        gridAutoRows: "100px",
                        gridAutoFlow: "dense",
                      }}
                    >
                      {visibleTiles.map((tile) => (
                        <ConfiguratorTile
                          key={tile.id}
                          tile={tile}
                          columns={columns}
                          onResize={handleResize}
                          onToggleVisibility={handleToggleVisibility}
                        />
                      ))}
                    </div>
                  </div>
                </VisibleGridDroppable>
              </SortableContext>
            ) : (
              <VisibleGridDroppable>
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <LayoutGrid className="size-8 text-muted-foreground/40" />
                    <div className="space-y-1">
                      <h3 className="text-sm font-medium text-muted-foreground">
                        No widgets on the board
                      </h3>
                      <p className="text-xs text-muted-foreground/70 max-w-[18rem]">
                        Drag widgets from the hidden panel or click the eye icon
                        to make them appear here.
                      </p>
                    </div>
                  </div>
                </div>
              </VisibleGridDroppable>
            )}
          </ScrollArea>

          <HiddenTilesSidebar
            sidebarRef={hiddenSidebarRef}
          />
        </div>

        {dragOverlayPortalReady
          ? createPortal(
              <DragOverlay dropAnimation={null} zIndex={100}>
                {activeTile ? (
                  <OverlayTile
                    tile={activeTile}
                    rect={activeTileRect}
                  />
                ) : null}
              </DragOverlay>,
              document.body
            )
          : null}
      </DndContext>

    </div>
  );
}

function OverlayTile({
  tile,
  rect,
}: {
  tile: TileConfig;
  rect: { width: number; height: number } | null;
}) {
  const bgColor = TILE_COLORS[tile.color] ?? TILE_COLORS.blue;
  const isFromHidden = !tile.visible;
  const useStripSize = isFromHidden;

  return (
    <div
      className="relative rounded-md overflow-hidden shadow-2xl"
      style={{
        width: useStripSize ? 156 : (rect?.width ?? 220),
        height: useStripSize ? 60 : (rect?.height ?? 100),
        backgroundColor: bgColor,
        opacity: 0.85,
      }}
    >
      <div className="relative h-full flex items-center justify-center px-4">
        <h3
          className={cn(
            "font-medium text-white/90 tracking-wide",
            useStripSize ? "text-[11px]" : "text-sm"
          )}
        >
          {tile.title}
        </h3>
      </div>
    </div>
  );
}
